import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useBillingAccounts, useLegacyTermSheet } from "hooks";
import { isDraftTermSheet, isActiveTermSheet, isInactiveTermSheet, hasValueChanged } from "constants/index";

/**
 * LegacyTermSheetProvider - Provides termsheet state and handlers to all child components
 *
 * @param {Object}    children - Child components
 * @param {Object}    termSheet - The termsheet data
 * @param {Object}    conversionMatrix - Currency conversion rates
 * @param {Array}     billingAccounts - Available billing accounts
 * @param {Boolean}   loadingBillingAccounts - Loading state for billing accounts
 * @param {Function}  onUpdate - Optional callback for when a cell is updated
 * @param {String}    buyInCurrency - Initial buy-in currency
 * @param {String}    sellInCurrency - Initial sell-in currency
 */
const LegacyTermSheetProvider = ({
  children,
  termSheet,
  conversionMatrix,
  billingAccounts,
  loadingBillingAccounts,
  onUpdate,
  buyInCurrency: initialBuyInCurrency,
  sellInCurrency: initialSellInCurrency,
  ...rest
}) => {
  const [changes, setChanges] = useState({});
  const [buyInCurrency, setBuyInCurrency] = useState(initialBuyInCurrency);
  const [sellInCurrency, setSellInCurrency] = useState(initialSellInCurrency);
  const [selectedBillingAccount, setSelectedBillingAccount] = useState(termSheet?.organizationBillingAccount?.id);

  // Fetch billing accounts and handle updates
  const { updating: updatingBillingAccount, handleBillingAccountChange: updateBillingAccount } = useBillingAccounts();

  // Get assignment CRUD operations
  const { handleSaveAssignments, updatingAssignments } = useLegacyTermSheet();

  // Wrap the handler to automatically pass termSheet
  const handleBillingAccountChange = useCallback(
    async (accountId) => await updateBillingAccount(accountId, termSheet),
    [updateBillingAccount, termSheet]
  );

  // Wrap save handler to automatically pass termSheet, changes, and setChanges
  const handleSave = useCallback(async () => {
    await handleSaveAssignments(termSheet, changes, () => setChanges({}));
  }, [handleSaveAssignments, termSheet, changes]);

  useEffect(() => {
    if (initialBuyInCurrency) {
      setBuyInCurrency(initialBuyInCurrency);
    }
  }, [initialBuyInCurrency]);

  useEffect(() => {
    if (initialSellInCurrency) {
      setSellInCurrency(initialSellInCurrency);
    }
  }, [initialSellInCurrency]);

  useEffect(() => {
    if (termSheet?.organizationBillingAccount?.id) {
      setSelectedBillingAccount(termSheet.organizationBillingAccount.id);
    }
  }, [termSheet?.organizationBillingAccount?.id]);

  const hasAnyChanges = useMemo(() => {
    // Check if ANY change is actually different from original value
    return Object.keys(changes).some((assignmentId) => {
      const assignment = termSheet?.assignments.find((a) => a.id === assignmentId);
      if (!assignment) return false;

      return Object.keys(changes[assignmentId] || {}).some((columnKey) => {
        const originalValue = assignment[columnKey];
        const changedValue = changes[assignmentId][columnKey];

        return hasValueChanged(originalValue, changedValue);
      });
    });
  }, [changes, termSheet?.assignments]);

  const isDraft = useMemo(() => isDraftTermSheet(termSheet), [termSheet]);
  const isActive = useMemo(() => isActiveTermSheet(termSheet), [termSheet]);
  const isInactive = useMemo(() => isInactiveTermSheet(termSheet), [termSheet]);

  const value = useMemo(
    () => ({
      termSheet,
      changes,
      setChanges,
      buyInCurrency,
      setBuyInCurrency,
      sellInCurrency,
      setSellInCurrency,
      selectedBillingAccount,
      conversionMatrix,
      billingAccounts,
      loadingBillingAccounts,
      updatingBillingAccount,
      handleBillingAccountChange,
      handleSave,
      saving: updatingAssignments,
      hasAnyChanges,
      onUpdate,
      isDraft,
      isActive,
      isInactive,
      ...rest,
    }),
    [
      termSheet,
      changes,
      buyInCurrency,
      sellInCurrency,
      selectedBillingAccount,
      conversionMatrix,
      billingAccounts,
      loadingBillingAccounts,
      updatingBillingAccount,
      handleBillingAccountChange,
      handleSave,
      updatingAssignments,
      hasAnyChanges,
      onUpdate,
      isDraft,
      isActive,
      isInactive,
      rest,
    ]
  );

  return <LegacyTermSheetContext.Provider value={value}>{children}</LegacyTermSheetContext.Provider>;
};

LegacyTermSheetProvider.propTypes = {
  children: PropTypes.node.isRequired,
  termSheet: PropTypes.object,
  conversionMatrix: PropTypes.object,
  billingAccounts: PropTypes.array,
  loadingBillingAccounts: PropTypes.bool,
  onUpdate: PropTypes.func,
  buyInCurrency: PropTypes.string,
  sellInCurrency: PropTypes.string,
};

const LegacyTermSheetContext = createContext({});

export { LegacyTermSheetContext };

export default LegacyTermSheetProvider;
